
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Link as LinkIcon, Trash2, Save, Search, Package, Store, DollarSign, Send, CreditCard } from 'lucide-react';
import { printInternalVoucher } from '../utils/printInternalVoucher';
import specialInventoryService from '../services/specialInventoryService';
import internalOrderService from '../services/internalOrderService';
import { customerService, walletService, heladeriaService } from '../services/api';
import toast from 'react-hot-toast';

const InternalPOSPage = () => {
    const navigate = useNavigate();

    // --- POS STATE ---
    const [localProducts, setLocalProducts] = useState([]);
    const [cart, setCart] = useState([]);
    const [loading, setLoading] = useState(false);

    // Customer selection
    const [customerSearch, setCustomerSearch] = useState('');
    const [customers, setCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Linking to Invoice
    const [invoiceSearch, setInvoiceSearch] = useState('');
    const [invoices, setInvoices] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    const [notes, setNotes] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('transferencia');

    // Heladería points
    const [heladeriaPoints, setHeladeriaPoints] = useState([]);
    const [selectedPoint, setSelectedPoint] = useState(null);

    const [searchTerm, setSearchTerm] = useState('');
    const searchInputRef = useRef(null);

    useEffect(() => {
        loadProducts();
        loadHeladeriaPoints();
        searchInputRef.current?.focus();
    }, []);

    const loadHeladeriaPoints = async () => {
        try {
            const res = await heladeriaService.getPoints();
            setHeladeriaPoints(res.data || []);
        } catch (err) {
            console.error('Error cargando puntos heladería:', err);
        }
    };

    const loadProducts = async () => {
        try {
            const data = await specialInventoryService.getLocalProducts();
            setLocalProducts(data.filter(p => p.is_active));
        } catch (error) {
            toast.error('Error al cargar productos');
        }
    };

    const filteredProducts = localProducts.filter(p =>
        p.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.barcode.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSearchCustomer = async (val) => {
        setCustomerSearch(val);
        if (val.length < 3) {
            setCustomers([]);
            return;
        }
        try {
            const response = await customerService.getCustomers({ search: val, limit: 5 });
            setCustomers(response.data.customers || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSearchInvoice = async (val) => {
        setInvoiceSearch(val);
        if (val.length < 3) {
            setInvoices([]);
            return;
        }
        try {
            // Search active orders for linking - RESTRICT TO PENDIENTE FACTURACION
            const response = await walletService.getWalletOrders({
                search: val,
                status: 'pendiente_por_facturacion'
            });
            setInvoices(response.data.orders || []);
        } catch (error) {
            console.error(error);
        }
    };

    const handleSelectInvoice = (inv) => {
        setSelectedInvoice(inv);
        setInvoices([]);
        setInvoiceSearch('');

        // AUTOMATICALLY SELECT CUSTOMER FROM INVOICE
        if (inv.customer_identification) {
            setSelectedCustomer({
                identification: inv.customer_identification,
                name: inv.customer_name
            });
        }
    };

    const addToCart = (product, isBundle = false) => {
        const existing = cart.find(item => item.id === product.id);
        if (existing) {
            if (existing.quantity + 1 > product.stock) {
                toast.error(`No hay suficiente inventario para ${product.product_name}`);
                return;
            }
            setCart(prev => prev.map(item => item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item));
        } else {
            if (product.stock < 1) {
                toast.error(`${product.product_name} agotado`);
                return;
            }
            setCart(prev => [...prev, { ...product, quantity: 1 }]);
        }

        // Bundle logic
        if (!isBundle && product.associated_milk_code) {
            const milk = localProducts.find(p => p.internal_code === product.associated_milk_code || p.barcode === product.associated_milk_code);
            if (milk) {
                setTimeout(() => {
                    addToCart(milk, true);
                    toast.success(`Combo: ${milk.product_name} agregado`);
                }, 200);
            }
        }
    };

    const removeFromCart = (id) => {
        setCart(cart.filter(item => item.id !== id));
    };

    const updateQuantity = (id, newQuantity) => {
        const qty = parseInt(newQuantity) || 0;
        const product = localProducts.find(p => p.id === id);

        if (qty < 1) {
            removeFromCart(id);
            return;
        }

        if (qty > product.stock) {
            toast.error(`Solo hay ${product.stock} unidades disponibles`);
            return;
        }

        setCart(cart.map(item =>
            item.id === id ? { ...item, quantity: qty } : item
        ));
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('es-CO').format(value || 0);
    };

    const internalTotal = cart.reduce((acc, item) => acc + (item.standard_price * item.quantity), 0);
    const siigoTotal = selectedInvoice?.total_amount || 0;
    const grandTotal = internalTotal + siigoTotal;

    const handleCreateOrder = async () => {
        if (cart.length === 0) {
            toast.error('Carrito vacío');
            return;
        }

        if (!selectedCustomer) {
            toast.error('Debe seleccionar un cliente o vincular una factura');
            return;
        }

        try {
            setLoading(true);
            const orderData = {
                customer_identification: selectedCustomer.identification,
                customer_name: selectedCustomer.name,
                items: cart.map(item => ({
                    product_id: item.id,
                    quantity: item.quantity,
                    price: item.standard_price
                })),
                parent_invoice_code: selectedInvoice?.order_number || null,
                payment_method: paymentMethod,
                notes: notes
            };

            const res = await internalOrderService.createInternalOrder(orderData);
            toast.success(`Orden creada: ${res.orderNumber}`);

            // If a heladería point was selected, link the remision
            if (selectedPoint && res.orderId) {
                try {
                    await heladeriaService.createRemision({ order_id: res.orderId, point_id: selectedPoint.id });
                    toast.success(`Remisión vinculada a ${selectedPoint.name}`);
                } catch (linkErr) {
                    console.error('Error vinculando remisión:', linkErr);
                    toast.error('Orden creada pero no se pudo vincular al punto');
                }
            }

            // Reset state
            setCart([]);
            setSelectedCustomer(null);
            setSelectedInvoice(null);
            setSelectedPoint(null);
            setNotes('');
            loadProducts(); // Reload stock

            // Redirect to order history
            navigate('/admin/orders/internal-history');

        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al crear orden');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="p-6 h-[calc(100vh-64px)] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center mb-6 flex-shrink-0">
                <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                    <ShoppingCart className="text-blue-600" />
                    Venta de Productos Especiales (POS Interno)
                </h1>
            </div>

            <div className="flex gap-6 flex-1 overflow-hidden">
                {/* Product Selection */}
                <div className="flex-1 bg-white rounded-xl shadow-sm p-4 overflow-y-auto border">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="font-bold text-gray-700 flex items-center gap-2">
                            <Package size={20} className="text-blue-500" /> Catálogo Local
                        </h2>
                        <div className="relative w-64 text-sm">
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="🔍 Buscar por nombre o código..."
                                className="w-full pl-9 pr-4 py-2 border bg-gray-50 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-2">
                        {filteredProducts.map(product => (
                            <button
                                key={product.id}
                                onClick={() => addToCart(product)}
                                className="text-left p-3 border rounded-xl hover:border-blue-500 hover:shadow-lg transition-all group relative overflow-hidden flex flex-col bg-white h-32 active:scale-95"
                            >
                                <div className="text-[10px] font-mono text-gray-400 mb-1 truncate tracking-tighter">{product.barcode}</div>
                                <div className="font-bold text-[13px] text-gray-800 line-clamp-2 leading-tight flex-1 group-hover:text-blue-700 transition-colors">
                                    {product.product_name}
                                </div>
                                <div className="mt-auto flex justify-between items-end pt-1">
                                    <div className="text-blue-600 font-extrabold text-[15px]">
                                        ${new Intl.NumberFormat().format(product.standard_price)}
                                    </div>
                                    <div className={`text-[11px] px-1.5 py-0.5 rounded font-black ${product.stock <= 5 ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                                        {product.stock}
                                    </div>
                                </div>
                                <div className="absolute top-0 right-0 p-1 opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100">
                                    <div className="bg-blue-600 text-white rounded-bl-xl px-2.5 py-1.5 text-xs font-black shadow-md">+</div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>

                {/* Sidebar Checkout */}
                <div className="w-96 bg-gray-50 rounded-xl border flex flex-col overflow-hidden h-full flex-shrink-0">
                    <div className="p-4 flex-1 overflow-y-auto">
                        <h2 className="font-bold text-lg mb-4">Checkout</h2>

                        {/* Customer Search */}
                        <div className="mb-4 relative">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">Cliente</label>
                            {selectedCustomer ? (
                                <div className="flex items-center justify-between bg-blue-50 p-2 rounded-lg border border-blue-200">
                                    <div className="flex items-center gap-2">
                                        <User size={16} className="text-blue-600" />
                                        <span className="text-sm font-medium">{selectedCustomer.name}</span>
                                    </div>
                                    <button onClick={() => setSelectedCustomer(null)} className="text-blue-600 hover:bg-blue-100 p-1 rounded">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ) : (
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Buscar cliente..."
                                        className="w-full pl-8 pr-4 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                                        value={customerSearch}
                                        onChange={(e) => handleSearchCustomer(e.target.value)}
                                    />
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    {customers.length > 0 && (
                                        <div className="absolute top-full left-0 w-full bg-white border shadow-lg rounded-b-lg z-10 mt-1 max-h-40 overflow-y-auto">
                                            {customers.map(c => (
                                                <button
                                                    key={c.id}
                                                    onClick={() => { setSelectedCustomer(c); setCustomers([]); setCustomerSearch(''); }}
                                                    className="w-full text-left p-2 hover:bg-gray-50 text-sm border-b last:border-0"
                                                >
                                                    <div className="font-medium">{c.name}</div>
                                                    <div className="text-xs text-gray-400">{c.identification}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Invoice Link */}
                        <div className="mb-4 relative">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">Vincular a Factura Siigo</label>
                            {selectedInvoice ? (
                                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                            <LinkIcon size={16} className="text-purple-600" />
                                            <span className="text-sm font-medium">{selectedInvoice.order_number}</span>
                                        </div>
                                        <button onClick={() => setSelectedInvoice(null)} className="text-purple-600 hover:bg-purple-100 p-1 rounded">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                    <div className="text-xs text-purple-700 font-semibold">
                                        Cliente: {selectedInvoice.customer_name}
                                    </div>
                                    <div className="text-sm font-bold text-purple-900 mt-1">
                                        Total Siigo: ${formatCurrency(selectedInvoice.total_amount)}
                                    </div>
                                </div>
                            ) : (
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder="Buscar FV-2-..."
                                        className="w-full pl-8 pr-4 py-2 text-sm border rounded-lg outline-none focus:ring-2 focus:ring-purple-500"
                                        value={invoiceSearch}
                                        onChange={(e) => handleSearchInvoice(e.target.value)}
                                    />
                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                                    {invoices.length > 0 && (
                                        <div className="absolute top-full left-0 w-full bg-white border shadow-lg rounded-b-lg z-10 mt-1 max-h-40 overflow-y-auto">
                                            {invoices.map(inv => (
                                                <button
                                                    key={inv.id}
                                                    onClick={() => handleSelectInvoice(inv)}
                                                    className="w-full text-left p-2 hover:bg-gray-50 text-sm border-b last:border-0"
                                                >
                                                    <div className="font-medium">{inv.order_number}</div>
                                                    <div className="text-xs text-gray-400">{inv.customer_name} - ${inv.total_amount}</div>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Heladería Point Selector */}
                        {heladeriaPoints.length > 0 && (
                            <div className="mb-4">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1 block">Punto Heladería (Remisión)</label>
                                {selectedPoint ? (
                                    <div className="flex items-center justify-between bg-emerald-50 p-2 rounded-lg border border-emerald-200">
                                        <div className="flex items-center gap-2">
                                            <Store size={16} className="text-emerald-600" />
                                            <div>
                                                <span className="text-sm font-medium">{selectedPoint.name}</span>
                                                <span className="text-xs text-emerald-600 ml-2">{selectedPoint.responsible_name}</span>
                                            </div>
                                        </div>
                                        <button onClick={() => setSelectedPoint(null)} className="text-emerald-600 hover:bg-emerald-100 p-1 rounded">
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-1.5">
                                        {heladeriaPoints.map(pt => (
                                            <button
                                                key={pt.id}
                                                onClick={() => setSelectedPoint(pt)}
                                                className="text-left p-2 border rounded-lg hover:border-emerald-400 hover:bg-emerald-50 transition-all text-sm flex items-center gap-2"
                                            >
                                                <Store size={14} className="text-emerald-500" />
                                                <div>
                                                    <span className="font-medium">{pt.name}</span>
                                                    <span className="text-xs text-gray-400 ml-1.5">({pt.responsible_name})</span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Cart Items */}
                        <div className="mt-6">
                            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">Resumen de Venta</label>
                            {cart.length === 0 ? (
                                <div className="text-center py-8 text-gray-400 border-2 border-dashed rounded-xl">
                                    No hay productos
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {cart.map(item => (
                                        <div key={item.id} className="bg-white p-3 rounded-lg border shadow-sm">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="flex-1 pr-2">
                                                    <div className="text-sm font-medium line-clamp-1">{item.product_name}</div>
                                                    <div className="text-xs text-gray-400">Stock: {item.stock}</div>
                                                </div>
                                                <button onClick={() => removeFromCart(item.id)} className="text-red-400 hover:text-red-600 p-1">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <label className="text-xs text-gray-500">Cantidad:</label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    max={item.stock}
                                                    value={item.quantity}
                                                    onChange={(e) => updateQuantity(item.id, e.target.value)}
                                                    className="w-20 px-2 py-1 text-sm border rounded focus:ring-2 focus:ring-blue-500 outline-none"
                                                />
                                                <span className="text-xs text-gray-500">x ${formatCurrency(item.standard_price)}</span>
                                                <span className="ml-auto text-sm font-bold text-gray-800">${formatCurrency(item.standard_price * item.quantity)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="p-4 bg-white border-t space-y-3">
                        <div className="space-y-2">
                            {selectedInvoice && (
                                <>
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span>Factura Siigo:</span>
                                        <span>${formatCurrency(siigoTotal)}</span>
                                    </div>
                                    <div className="flex justify-between text-sm text-gray-600">
                                        <span>Pedido Interno:</span>
                                        <span>${formatCurrency(internalTotal)}</span>
                                    </div>
                                    <div className="border-t pt-2"></div>
                                </>
                            )}
                            <div className="flex justify-between items-center text-lg font-bold">
                                <span>GRAN TOTAL:</span>
                                <span className="text-blue-600">${formatCurrency(selectedInvoice ? grandTotal : internalTotal)}</span>
                            </div>
                        </div>

                        {/* Payment Method Selector */}
                        <div className="space-y-2 py-2">
                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block">Método de Pago</label>
                            <div className="grid grid-cols-3 gap-2">
                                <button
                                    onClick={() => setPaymentMethod('efectivo')}
                                    className={`py-2 px-1 text-xs font-bold rounded-lg border transition-all flex flex-col items-center justify-center gap-1 ${paymentMethod === 'efectivo' ? 'bg-green-50 border-green-500 text-green-700 shadow-sm' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                                >
                                    <DollarSign size={16} />
                                    <span>Efectivo</span>
                                </button>
                                <button
                                    onClick={() => setPaymentMethod('transferencia')}
                                    className={`py-2 px-1 text-xs font-bold rounded-lg border transition-all flex flex-col items-center justify-center gap-1 ${paymentMethod === 'transferencia' ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                                >
                                    <Send size={16} />
                                    <span>Transf.</span>
                                </button>
                                <button
                                    onClick={() => setPaymentMethod('mercado_pago')}
                                    className={`py-2 px-1 text-xs font-bold rounded-lg border transition-all flex flex-col items-center justify-center gap-1 ${paymentMethod === 'mercado_pago' ? 'bg-indigo-50 border-indigo-500 text-indigo-700 shadow-sm' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'}`}
                                >
                                    <CreditCard size={16} />
                                    <span>MercadoP.</span>
                                </button>
                            </div>
                        </div>

                        <textarea
                            placeholder="Notas opcionales..."
                            className="w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none h-20"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                        />
                        <button
                            disabled={loading || cart.length === 0 || !selectedCustomer}
                            onClick={handleCreateOrder}
                            className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${loading || cart.length === 0 || !selectedCustomer
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98] shadow-lg shadow-blue-200'
                                }`}
                        >
                            <Save size={20} />
                            {loading ? 'Procesando...' : 'CREAR PEDIDO'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InternalPOSPage;
